import React, { useEffect, useState } from "react";
import {ThunkDispatch} from "@reduxjs/toolkit";
import {useSelector, useDispatch} from 'react-redux'
import {useLocation, useNavigate} from "react-router-dom"
import LoginHeader from "../components/LoginHeader"
import TextInput from '../components/TextInput';
import PasswordInput from '../components/PasswordInput';
import LogoHeader from "../components/LogoHeader";
import CustomButton from '../components/CustomButton';
import NotFoundPage from "../pages/NotFoundPage";
import { confirmTalentRegistration, getTalentConfirmRegistrationInfo, resetConfirmState } from "../reducers/talentRegister";
import { validatePassword, validateFieldReq } from "../utils/validation";
import CustomTag from "../components/CustomTag";
import PolicySection from "./talent_view/components/PolicySection";
import logo from "../../assets/img/logo_20250311@2x.png"
import { closeSnackbar, enqueueSnackbar } from "notistack";
import CustomToast from "../components/CustomToast";
import { currentUserActions } from "../reducers/currentUser";
import LoginSubmitCode from "../components/LoginSubmitCode";
import i18n from 'i18next';
import {TALENT_TEMP_STATUS} from '../constants/index';

const getTokenFromUrl = (): string | null => {
  if (typeof window !== "undefined" && window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const hashToken = hashParams.get('t') || hashParams.get('token');
    if (hashToken) return hashToken;
  }
  if (typeof window !== "undefined" && window.location.search) {
    const queryParams = new URLSearchParams(window.location.search);
    const queryToken = queryParams.get('t') || queryParams.get('token');
    if (queryToken) return queryToken;
  }
  return null;
};

const TalentConfirmRegistration = ({view}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();
  const currentUser = useSelector((state: any) => state.currentUser)
  const { confirmRegistrationData, confirmRegistrationLoading, actionStatus, actionName, isSuccess, resMessage, isReloadConfirmRegistrationInfo } = useSelector((state: any) => state.talentRegister)
  const {isAuthCodeCreated} = currentUser
  const location = useLocation();
  const token = getTokenFromUrl();
  const [isAgree, setIsAgree] = useState(false);

  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    password: "",
    identity_verification: "",
  });
  const firstName = confirmRegistrationData?.first_name || "";
  const lastName = confirmRegistrationData?.last_name || "";
  const email = confirmRegistrationData?.email || "";
  const companyName = confirmRegistrationData?.company_name || "";
  const companyLogo = confirmRegistrationData?.company_logo || "";
  const identityVerificationLabel = confirmRegistrationData?.identity_verification_label || "";
  const [password, setPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const showToast = (title: string, message: string, variant: string, textLink: string = "", clickOnText: any = "") => {
    enqueueSnackbar("I love snacks.", {
      autoHideDuration: 7000,
      content: (key) => (
        <CustomToast title={title} message={message} variant={variant} textLink={textLink} clickOnText={clickOnText} snackbarKey={key} closeSnackbar={closeSnackbar} />
      ),
    });
  };

  useEffect(() => {
    dispatch(resetConfirmState());
    setPassword("");
    setIsAgree(false);
    setIsSaving(false);
    setErrors({
      first_name: "",
      last_name: "",
      password: "",
      identity_verification: "",
    });

    if (!token) {
      return;
    }

    let isCurrentRequest = true;
    dispatch(getTalentConfirmRegistrationInfo(token))
      .unwrap()
      .then((data: any) => {
        if (isCurrentRequest && data?.status === TALENT_TEMP_STATUS.REGISTERED) {
          navigate("/talent/login", { replace: true });
        }
      })
      .catch(() => {});

    return () => {
      isCurrentRequest = false;
    };
  }, [dispatch, location.hash, location.search, navigate, token]);

  useEffect(() => {
    if (actionStatus === 'fulfilled') {
      switch(actionName) {
        case 'confirmTalentRegistration':
          if (isSuccess) {
            dispatch(currentUserActions.genAuthCodeUser({email, password, view, navigate}))
          } else {
            setIsSaving(false);
            if(isReloadConfirmRegistrationInfo) {
              showToast("メッセージ", i18n.t('MSG_IS_TALENT_REGISTRATION_FORM_NEED_RELOAD'), "error");
              return;
            } else if(resMessage) {
              setErrors(prev => ({...prev, identity_verification: resMessage}))
              showToast("メッセージ", resMessage, isSuccess ? "success" : "error");
            }
          }
          break;
      }
    }
  }, [actionStatus, actionName, resMessage, confirmRegistrationData, navigate]);

  if (!token || (actionName === "getTalentConfirmRegistrationInfo" && actionStatus === "rejected")) {
    return <NotFoundPage />;
  }

  if (confirmRegistrationData?.status === TALENT_TEMP_STATUS.REGISTERED) {
    return null;
  }

  const staffRegister = (event: any) => {
    event.preventDefault()
    const formData = new FormData(event.target);
    const password = formData.get('password');
    const first_name = formData.get('first_name');
    const last_name = formData.get('last_name');
    const identityVerificationTextValue = formData.get('identity_verification');

    if (validateFormData(formData)) {
      setIsSaving(true);
      dispatch(confirmTalentRegistration({
        token,
        password,
        first_name,
        last_name,
        identity_verification_text: identityVerificationTextValue,
      }))
    }
  }

  const handleChangeAgree = () => {
    setIsAgree(prev => !prev)
  };

  const validateFormData = (formData: FormData) => {
    let formErrors = {
      first_name: "",
      last_name: "",
      password: "",
      identity_verification: "",
    };

    const validateLastnameRes = validateFieldReq(formData.get('last_name'), "姓");
    if(validateLastnameRes != true) {
      formErrors.last_name = String(validateLastnameRes);
    }

    const validateFirstnameRes = validateFieldReq(formData.get('first_name'), "名");
    if(validateFirstnameRes != true) {
      formErrors.first_name = String(validateFirstnameRes);
    }

    const validatePasswordRes = validatePassword(formData.get('password'))
    if (validatePasswordRes != true) {
      formErrors.password = String(validatePasswordRes);
    }

    const validateIdentityVerificationRes = identityVerificationLabel ? validateFieldReq(formData.get('identity_verification'), identityVerificationLabel) : true;
    if (validateIdentityVerificationRes != true) {
      formErrors.identity_verification = `${identityVerificationLabel}を入力してください。`;
    }

    setErrors(formErrors);
    return !Object.values(formErrors).some((error) => error);
  };

  return (
    <>
      {confirmRegistrationData && !isAuthCodeCreated && (
        <div className="min-h-screen w-full overflow-y-auto flex items-center justify-center md:p-4">
          <div className="w-full md:max-w-[23.75rem] md:min-w-[24.75rem]">
            <div className="bg-white rounded-2xl md:shadow-lg w-full py-4 md:py-6">
              {companyLogo &&
                <img src={companyLogo} alt="logo" className="mx-auto w-auto h-[3.1875rem] object-contain" />
              }
              <div className="px-4 mt-3">
                <p className="text-base text-[#FF861A] font-bold">{companyName} のタレントネットワークへようこそ！</p>
                <p className="text-sm alu-text-base-2">表示されている名前がご自身のものであることをご確認の上、ご登録ください！</p>
              </div>
              <div className="flex flex-col items-center justify-center mt-6 md:mt-6 px-3">
                <form onSubmit={staffRegister} className="bg-white w-full" autoComplete="off">
                  <div className="mb-4">
                    <label
                      className="flex items-center mb-2 text-sm gap-1 pointer-events-none alu-text-base-2"
                      htmlFor="companyName"
                    >
                      登録先企業
                    </label>
                    <TextInput
                      disabled
                      id="companyName"
                      name="companyName"
                      placeholder="rakumo 株式会社"
                      defaultValue={companyName}
                      autoComplete="off"
                      required
                      customClassName="rounded-lg !bg-[#F4F4F4] font-bold"
                    />
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm pointer-events-none leading-none alu-text-base-2" htmlFor="fullname">氏名</label>
                      <CustomTag type="required" />
                    </div>
                    <div className="flex items-center gap-2 pr-2">
                      <div className="w-full">
                        <TextInput
                          id="last_name"
                          name="last_name"
                          placeholder="姓"
                          defaultValue={lastName}
                          type="text"
                          autoComplete="off"
                          error={errors.last_name}
                          customClassName="rounded-lg"
                        />
                      </div>
                      <div className="w-full">
                        <TextInput
                          id="first_name"
                          name="first_name"
                          placeholder="名"
                          defaultValue={firstName}
                          type="text"
                          autoComplete="off"
                          error={errors.first_name}
                          customClassName="rounded-lg"
                        />
                      </div>
                    </div>
                    {(errors.first_name || errors.last_name) && (
                      <div className="flex items-center gap-2">
                        <span className="alu-text-error text-sm mt-2 w-[50%]">{errors.last_name}</span>
                        <span className="alu-text-error text-sm mt-2 w-[50%]">{errors.first_name}</span>
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block mb-2 text-sm pointer-events-none alu-text-base-2" htmlFor="email">
                      メールアドレス
                    </label>
                    <div className="pr-2">
                      <TextInput
                        id="email"
                        name="email"
                        placeholder="入力してください"
                        defaultValue={email}
                        autoComplete="off"
                        disabled
                        customClassName="rounded-lg !bg-[#F4F4F4] font-bold"
                      />
                    </div>
                  </div>

                  <div className={`${identityVerificationLabel ? "mb-4" : "mb-10"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm pointer-events-none leading-none alu-text-base-2" htmlFor="password">パスワードを設定</label>
                      <CustomTag type="required" />
                    </div>
                    <div className="pr-2">
                      <PasswordInput
                        id="password"
                        name="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="8文字以上で英数字を含めてください"
                        autoComplete="new-password"
                        error={errors.password.length > 0}
                        errorText={errors.password}
                        customClassName="rounded-lg"
                      />
                    </div>
                  </div>

                  {identityVerificationLabel && (
                    <div className="mb-10">
                      <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm pointer-events-none leading-none alu-text-base-2" htmlFor="identity_verification">{identityVerificationLabel}</label>
                        <CustomTag type="required" />
                      </div>
                      <div className="pr-2">
                        <TextInput
                          id="identity_verification"
                          name="identity_verification"
                          placeholder="入力してください"
                          autoComplete="off"
                          maxLength={100}
                          error={Boolean(errors.identity_verification)}
                          errorText={errors.identity_verification}
                          customClassName="rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                  <PolicySection isAgree={isAgree} handleChangeAgree={handleChangeAgree} />
                  <CustomButton
                    type="submit"
                    width='full'
                    disabled={confirmRegistrationLoading || !isAgree || isSaving}
                    customStyle="mt-2 md:mt-6"
                  >
                    {confirmRegistrationLoading ? "処理中..." : "登録"}
                  </CustomButton>
                </form>
              </div>
              <div className="flex items-center justify-center gap-2 mt-9">
                <p className="text-xs text-[#000]">powered by. </p>
                <img src={logo} alt="aloop logo" width='68' height='18'
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {isAuthCodeCreated && (
        <LoginSubmitCode
          view={view}
          onCancel={() => {
            dispatch(currentUserActions.setInitState());
            dispatch(resetConfirmState());
            navigate("/talent/login", { replace: true });
          }}
        />
      )}
    </>
  );
};

export default TalentConfirmRegistration;